import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import ReadingsGrid from "@/components/ReadingsGrid";
import ClosingPhotoCapture from "@/components/ClosingPhotoCapture";
import { useProducts, unitLabel } from "@/lib/products";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, FlaskConical, UserCheck, Wallet, UserPlus } from "lucide-react";
import { fmtINR, fmtNum } from "@/utils/format";

const C_EMPTY = { paytm: 0, credit: 0, card: 0 };

function computeSide(opening, closing, testing, prices, products) {
  let grossTotal = 0, testTotal = 0, netTotal = 0, total = 0;
  for (const p of products) {
    const gross = Math.max(0, p.nozzles.reduce((s, nz) => s + (Number(closing[nz] || 0) - Number(opening[nz] || 0)), 0));
    const test = Number(testing[p.key] || 0);
    const net = gross - test;
    grossTotal += gross; testTotal += test; netTotal += net; total += net * Number(prices[p.key] || 0);
  }
  return { grossTotal, testTotal, netTotal, total };
}

const numTesting = (t, products) => ({ ...Object.fromEntries(products.map((p) => [p.key, Number(t[p.key] || 0)])), remarks: t.remarks || null });

const expectedCash = (sale, coll) =>
  Math.max(0, sale - Number(coll.paytm || 0) - Number(coll.credit || 0) - Number(coll.card || 0));

export default function EndShift() {
  const { id } = useParams();
  const nav = useNavigate();
  const [shift, setShift] = useState(null);
  const products = useProducts(shift);
  const [closing, setClosing] = useState({});
  const [closing2, setClosing2] = useState({});
  const [testing, setTesting] = useState({ remarks: "" });
  const [testing2, setTesting2] = useState({ remarks: "" });
  const [collection, setCollection] = useState({ ...C_EMPTY });
  const [collection2, setCollection2] = useState({ ...C_EMPTY });
  const [nextMpd1, setNextMpd1] = useState("");
  const [nextMpd2, setNextMpd2] = useState("");
  const [avail, setAvail] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/shifts/${id}`).then((r) => setShift(r.data));
    api.get("/employees/available").then((r) => setAvail(r.data)).catch(() => {});
  }, [id]);

  const preview = useMemo(() => {
    if (!shift) return null;
    const p = shift.fuel_prices_used;
    const s1 = computeSide(shift.opening, closing, testing, p, products);
    const s2 = shift.opening_mpd2 ? computeSide(shift.opening_mpd2, closing2, testing2, p, products) : null;
    return {
      s1, s2,
      grossTotal: s1.grossTotal + (s2?.grossTotal || 0),
      testTotal: s1.testTotal + (s2?.testTotal || 0),
      netTotal: s1.netTotal + (s2?.netTotal || 0),
      total: s1.total + (s2?.total || 0),
    };
  }, [shift, closing, testing, closing2, testing2]);

  if (!shift) return <StaffLayout><div className="h-32 animate-pulse bg-slate-100 rounded-2xl" /></StaffLayout>;

  const isDual = !!shift.opening_mpd2;
  const isHandover = shift.status === "handover_accepted";

  const validateSide = (o, c, label = "") => {
    for (const k of products.flatMap((p) => p.nozzles)) {
      if (c[k] === "" || c[k] === undefined || isNaN(Number(c[k]))) {
        toast.error(`${label ? label + " — " : ""}All closing readings are required`);
        return false;
      }
      if (Number(c[k]) < o[k]) {
        toast.error(`${label ? label + " — " : ""}${k.replace("_", " ").toUpperCase()}: closing < opening`);
        return false;
      }
    }
    return true;
  };

  const photosOk = !!shift.closing_photo && (!isDual || !!shift.closing_photo_mpd2);

  const submit = async (e) => {
    e.preventDefault();
    if (!photosOk) return toast.error("Please take the closing slip photo first");
    if (!validateSide(shift.opening, closing, isDual ? "MPD 1" : "")) return;
    if (isDual && !validateSide(shift.opening_mpd2, closing2, "MPD 2")) return;
    if (isDual && nextMpd1 && nextMpd2 && nextMpd1 === nextMpd2) return toast.error("Same employee assigned to both MPDs");
    setBusy(true);
    try {
      const body = {
        closing: Object.fromEntries(Object.entries(closing).map(([k, v]) => [k, Number(v)])),
        testing: numTesting(testing, products),
        collection: {
          paytm: Number(collection.paytm || 0),
          credit: Number(collection.credit || 0),
          card: Number(collection.card || 0),
        },
      };
      if (isDual) {
        body.closing_mpd2 = Object.fromEntries(Object.entries(closing2).map(([k, v]) => [k, Number(v)]));
        body.testing_mpd2 = numTesting(testing2, products);
        body.collection_mpd2 = {
          paytm: Number(collection2.paytm || 0),
          credit: Number(collection2.credit || 0),
          card: Number(collection2.card || 0),
        };
        if (nextMpd1) body.next_employee_id_mpd1 = nextMpd1;
        if (nextMpd2) body.next_employee_id_mpd2 = nextMpd2;
      }
      const { data } = await api.post(`/shifts/${id}/end`, body);
      const handovers = (data.new_shifts || []).length;
      toast.success(handovers ? `Shift completed · ${handovers} handover${handovers > 1 ? "s" : ""} started` : "Shift completed");
      nav(`/shift/${data.ended_shift.id}/summary`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const availableList = avail.filter((e) => e.id !== shift.employee_id);

  return (
    <StaffLayout>
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <h1 className="text-2xl font-display font-bold">{isHandover ? "Complete Handover" : "End Shift"}</h1>
      </div>

      {isHandover && (
        <Card className="p-4 rounded-2xl border-emerald-500 border-2 bg-emerald-50 mb-5" data-testid="handover-banner">
          <div className="flex items-center gap-2 text-sm">
            <UserCheck className="w-4 h-4 text-emerald-700" />
            <span>Handing over <b>{shift.mpd}</b> to <b>{shift.pending_next_employee_name}</b>.</span>
          </div>
        </Card>
      )}

      <form onSubmit={submit} className="space-y-5">
        <ClosingPhotoCapture
          shiftId={shift.id} side="1"
          label={isDual ? "MPD 1 · Closing Slip Photo" : "Closing Slip Photo"}
          existing={!!shift.closing_photo}
          onUploaded={(p) => setShift((s) => ({ ...s, closing_photo: p }))}
        />
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-3">
            {isDual ? "MPD 1 · Closing Readings" : "Closing Readings"}
          </div>
          <ReadingsGrid products={products} values={closing} opening={shift.opening} onChange={(k, v) => setClosing((r) => ({ ...r, [k]: v }))} prefix="closing" />
        </div>
        <TestingBlock products={products} label={isDual ? "MPD 1 · Fuel Testing (L)" : "Fuel Testing (Litres)"} values={testing} onChange={setTesting} testIdPrefix="test" />
        <CollectionBlock
          label={isDual ? "MPD 1 · Collection Received" : "Collection Received"}
          sale={preview.s1.total}
          values={collection}
          onChange={setCollection}
          testIdPrefix="collect"
        />

        {isDual && (
          <>
            <ClosingPhotoCapture
              shiftId={shift.id} side="2" label="MPD 2 · Closing Slip Photo"
              existing={!!shift.closing_photo_mpd2}
              onUploaded={(p) => setShift((s) => ({ ...s, closing_photo_mpd2: p }))}
            />
            <div>
              <div className="text-sm font-semibold text-slate-700 mb-3">MPD 2 · Closing Readings</div>
              <ReadingsGrid products={products} values={closing2} opening={shift.opening_mpd2} onChange={(k, v) => setClosing2((r) => ({ ...r, [k]: v }))} prefix="closing2" />
            </div>
            <TestingBlock products={products} label="MPD 2 · Fuel Testing (L)" values={testing2} onChange={setTesting2} testIdPrefix="test2" />
            <CollectionBlock
              label="MPD 2 · Collection Received"
              sale={preview.s2?.total || 0}
              values={collection2}
              onChange={setCollection2}
              testIdPrefix="collect2"
            />

            <Card className="p-5 rounded-2xl border-slate-200" data-testid="split-handover-block">
              <div className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                <UserPlus className="w-4 h-4 text-[color:var(--bpcl-blue)]" /> Handover next MPDs (optional)
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <HandoverPicker label="MPD 1 → next" list={availableList} exclude={nextMpd2} value={nextMpd1} onChange={setNextMpd1} testId="handover-mpd1" />
                <HandoverPicker label="MPD 2 → next" list={availableList} exclude={nextMpd1} value={nextMpd2} onChange={setNextMpd2} testId="handover-mpd2" />
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Skip either dropdown if that MPD is not being handed over.
              </div>
            </Card>
          </>
        )}

        {preview && (
          <Card className="p-5 rounded-2xl border-slate-200 bg-slate-50">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-3">Live totals {isDual && "(both MPDs)"}</div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="text-[10px] uppercase text-slate-500">Gross</div><div className="font-mono-num font-semibold">{fmtNum(preview.grossTotal)} {unitLabel(products)}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Testing</div><div className="font-mono-num font-semibold">{fmtNum(preview.testTotal)} {unitLabel(products)}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Net</div><div className="font-mono-num font-semibold">{fmtNum(preview.netTotal)} {unitLabel(products)}</div></div>
            </div>
            <div className="mt-4 border-t border-slate-200 pt-4 text-center">
              <div className="text-[10px] uppercase text-slate-500">Grand Sales</div>
              <div className="text-2xl font-bold font-mono-num text-[color:var(--bpcl-blue)] mt-1" data-testid="live-total">{fmtINR(preview.total)}</div>
            </div>
          </Card>
        )}

        <Button
          type="submit" disabled={busy || !photosOk} data-testid="finish-shift-btn"
          className="w-full h-14 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)] text-base font-semibold"
        >
          <CheckCircle2 className="w-5 h-5 mr-2" />
          {busy ? "Saving…" : isHandover ? "COMPLETE HANDOVER" : "FINISH SHIFT & PRINT"}
        </Button>
      </form>
    </StaffLayout>
  );
}

// Defined at module level so inputs keep focus between keystrokes (no remount).
function TestingBlock({ label, values, onChange, testIdPrefix, products }) {
  return (
    <Card className="p-5 rounded-2xl border-slate-200">
      <div className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
        <FlaskConical className="w-4 h-4 text-[color:var(--bpcl-orange-dark)]" />
        {label}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {products.map((p) => { const k = p.key; return (
          <div key={k} className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">{p.short} ({p.unit})</Label>
            <Input
              data-testid={`${testIdPrefix}-${k}`}
              type="number" step="0.01" min="0" inputMode="decimal"
              value={values[k] ?? 0}
              onChange={(e) => onChange({ ...values, [k]: e.target.value })}
              className="h-12 rounded-xl font-mono-num" placeholder="0"
            />
          </div>
        ); })}
      </div>
      <div className="mt-3">
        <Label className="text-xs uppercase tracking-wide text-slate-500">Remarks</Label>
        <Textarea
          data-testid={`${testIdPrefix}-remarks`}
          value={values.remarks}
          onChange={(e) => onChange({ ...values, remarks: e.target.value })}
          placeholder="Optional"
          className="mt-1.5 rounded-xl" rows={2}
        />
      </div>
    </Card>
  );
}

function CollectionBlock({ label, sale, values, onChange, testIdPrefix }) {
  const cash = expectedCash(sale, values);
  return (
    <Card className="p-5 rounded-2xl border-slate-200" data-testid={`${testIdPrefix}-block`}>
      <div className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
        <Wallet className="w-4 h-4 text-emerald-700" /> {label}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { k: "paytm", l: "Paytm / UPI" },
          { k: "credit", l: "Credit / Udhar" },
          { k: "card", l: "Card" },
        ].map(({ k, l }) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">{l}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
              <Input
                data-testid={`${testIdPrefix}-${k}`}
                type="number" step="0.01" min="0" inputMode="decimal"
                value={values[k]}
                onChange={(e) => onChange({ ...values, [k]: e.target.value })}
                className="h-12 rounded-xl pl-7 font-mono-num" placeholder="0"
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase text-emerald-800 tracking-wide">Expected Cash · auto</div>
          <div className="text-xs text-emerald-700">Total Sale − (Paytm + Credit + Card)</div>
        </div>
        <div className="text-2xl font-bold font-mono-num text-emerald-700" data-testid={`${testIdPrefix}-cash`}>
          {fmtINR(cash)}
        </div>
      </div>
    </Card>
  );
}

function HandoverPicker({ label, list, exclude, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
        <SelectTrigger data-testid={testId} className="h-12 rounded-xl mt-1">
          <SelectValue placeholder="Select employee" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— No handover —</SelectItem>
          {list.map((e) => (
            <SelectItem
              key={e.id}
              value={e.id}
              disabled={!e.available || e.id === exclude}
            >
              {e.name} ({e.employee_id}){!e.available ? ` · running ${e.current_mpd}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
